<template>
  <coozzy-card
    :title="$t('message.settings.calendar.title')">
    <div
      v-if="loading"
      style="height: 30px">
      <coozzy-spinner />
    </div>
    <div
      v-else
      class="row m-0">
      <div class="col-12">
        <coozzy-form-checkbox
          v-model="addEmail"
          :initial="addEmail"
          :disabled="emailConnected">
          {{ $t('message.settings.calendar.addEmail') }}
        </coozzy-form-checkbox>
      </div>
      <div class="col-12">
        <coozzy-form-checkbox
          v-model="addCalendar"
          :initial="addCalendar"
          :disabled="calendarConnected">
          {{ $t('message.settings.calendar.addCalendar') }}
        </coozzy-form-checkbox>
      </div>
      <div class="col-12 mt-2">
        <div class="row">
          <div
            v-if="emailConnected || calendarConnected"
            class="col-12 col-sm-6 col-md-3">
            <coozzy-form-input
              v-model="emailAddress"
              :name="$tc('message.contact.preferredCommunicationChannelType.email')"
              type="text"
              :disabled="emailConnected" />
          </div>
          <div
            v-if="calendarConnected"
            class="col-12 col-sm-6 col-md-3 mt-2">
            <coozzy-button
              class="mt-3"
              @click="showCreateCalendar">
              {{ $t('message.calendar.changeCalendar') }}
            </coozzy-button>
          </div>
        </div>
      </div>
      <div class="col-12 mt-3 d-flex">
        <div
          v-if="!emailConnected || !calendarConnected"
          class="align-content-center">
          <coozzy-button
            class="pr-2 pl-2"
            design="green"
            :disabled="!(addEmail !== emailConnected || addCalendar !== calendarConnected)"
            @click="connectAccount()">
            {{ $t('message.settings.calendar.connectAccount') }}
          </coozzy-button>
        </div>
        <div
          class="col"
          :class="emailConnected && calendarConnected ? 'pl-0' : ''">
          <coozzy-alert
            v-if="emailConnected && !calendarConnected"
            variant="success"
            class="mb-0 pt-2 pb-2">
            {{ $t('message.settings.calendar.emailAlreadyConnected') }}
            <span
              class="link ml-3"
              @click="showUnlinkAccountModal()">
              {{ $t('message.settings.calendar.change') }}
            </span>
          </coozzy-alert>
          <coozzy-alert
            v-if="!emailConnected && calendarConnected"
            variant="success"
            class="mb-0 pt-2 pb-2">
            {{ $t('message.settings.calendar.calendarAlreadyConnected') }}
            <span
              class="link ml-3"
              @click="showUnlinkAccountModal()">
              {{ $t('message.settings.calendar.change') }}
            </span>
          </coozzy-alert>
          <coozzy-alert
            v-if="emailConnected && calendarConnected"
            variant="success"
            class="mb-0 pt-2 pb-2">
            {{ $t('message.settings.calendar.accountAlreadyConnected') }}
            <span
              class="link ml-3"
              @click="showUnlinkAccountModal()">
              {{ $t('message.settings.calendar.change') }}
            </span>
          </coozzy-alert>
        </div>
      </div>
    </div>
    <b-modal
      v-if="showCreateCalendarModal"
      :id="'create-calendar-modal'"
      :ref="'create-calendar-modal'"
      :title="$tc('message.calendar.selectCalendar')">
      <div class="col-12 col-sm-12 mb-2">
        <label for="selectingNewCalendar">{{ $tc('message.navigation.calendar.title') }} </label>
        <coozzy-form-select
          id="selectingNewCalendar"
          v-model="calendarForCreation">
          <option
            v-for="(item, index) in availableCalendars"
            :key="index"
            :value="item.nylasCalendarId"
            :disabled="calendarOfUser && item.nylasCalendarId === calendarOfUser.nylasCalendarId">
            {{ item.name }}
          </option>
        </coozzy-form-select>
      </div>
      <template #modal-footer>
        <div class="w-100 float-right">
          <coozzy-button
            design="green"
            class="float-right ml-3"
            :disabled="calendarForCreation === '' || savingCalendar"
            @click="setCalendar()">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
          <coozzy-button
            design="default"
            :disabled="savingCalendar"
            @click="hideCreateCalendarModal">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      v-if="showUnlinkAccountModalBool"
      :id="'unlink-account-modal'"
      :ref="'unlink-account-modal'"
      :title="$tc('message.settings.calendar.unlinkAccountModal.title')">
      <div class="col-12 col-sm-12 mb-2">
        {{ $t('message.settings.calendar.unlinkAccountModal.description') }}
      </div>
      <template #modal-footer>
        <div class="w-100 float-right">
          <coozzy-button
            design="green"
            class="float-right ml-3"
            @click="unlinkConnection()">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
          <coozzy-button
            design="default"
            @click="hideUnlinkAccountModal()">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
  </coozzy-card>
</template>
<script lang="ts">
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import { user } from '@/mixins/user'
import Vue from 'vue'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import CalendarApi from '@/misc/apis/CalendarApi'
import { NylasCalendar, NylasAuthGetMyConfigResponse, NylasAuthScope } from '@/types/api'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import CoozzyAlert from '@/framework/components/alert/CoozzyAlert.vue'

export default Vue.extend({
  name: 'CalendarSettings',
  components: {
    CoozzyAlert,
    CoozzyFormSelect,
    CoozzyFormInput,
    CoozzyFormCheckbox,
    CoozzyButton,
    CoozzySpinner,
    CoozzyCard
  },
  mixins: [user],
  data() {
    return {
      loading: false,
      addEmail: false,
      addCalendar: false,
      userConfig: null as NylasAuthGetMyConfigResponse,
      emailAddress: '',
      availableCalendars: [] as NylasCalendar[],
      calendarOfUser: null as NylasCalendar,
      showCreateCalendarModal: false,
      calendarForCreation: '',
      savingCalendar: false,
      showUnlinkAccountModalBool: false
    }
  },
  computed: {
    calendarConnected() {
      return this.userConfig !== null && this.userConfig.scopes.length > 0 && this.userConfig.scopes.includes('SCOPE_CALENDAR')
    },
    emailConnected() {
      return this.userConfig !== null && this.userConfig.scopes.length > 0 && this.userConfig.scopes.includes('SCOPE_EMAIL')
    }
  },
  mounted: function () {
    this.loading = true
    this.getUserConfig()
  },
  methods: {
    async getUserConfig() {
      try {
        const response = await CalendarApi.getUserConfig()
        this.userConfig = response
        if (response.scopes.length > 0) {
          this.addEmail = response.scopes.includes(NylasAuthScope.SCOPE_EMAIL)
          this.addCalendar = response.scopes.includes(NylasAuthScope.SCOPE_CALENDAR)
        }
        if (this.addEmail) {
          this.emailAddress = response.email
        } else {
          this.emailAddress = ''
        }
        this.loading = false
        if (localStorage.getItem('startNylasAuth') === 'true') {
          localStorage.removeItem('startNylasAuth')
          await this.showCreateCalendar()
        } else {
          const calendarResponse = await CalendarApi.listCalendarsByUserIds([this.userId])
          if (calendarResponse.calendars.length > 0) {
            this.calendarOfUser = calendarResponse.calendars[0]
          } else {
            this.calendarOfUser = null
          }
        }
      } catch (error) {
        if (error.response?.data?.message?.includes('No Nylas grant found for user ID')) {
          this.userConfig = null
          this.addEmail = false
          this.addCalendar = false
          this.emailAddress = ''
          this.calendarOfUser = null
          this.loading = false
          return
        }
        console.log(error)
        this.loading = false
      }
    },
    async showCreateCalendar() {
      this.availableCalendars = (await CalendarApi.listAvailableCalendars(this.userId)).calendars
      this.showCreateCalendarModal = true
      this.calendarForCreation = this.calendarOfUser ? this.calendarOfUser.nylasCalendarId : ''
      this.$nextTick(() => {
        if (this.$refs['create-calendar-modal']) {
          this.$refs['create-calendar-modal'].show()
        }
      })
    },
    showUnlinkAccountModal() {
      this.showUnlinkAccountModalBool = true
      this.$nextTick(() => {
        if (this.$refs['unlink-account-modal']) {
          this.$refs['unlink-account-modal'].show()
        }
      })
    },
    hideUnlinkAccountModal() {
      if (this.$refs['unlink-account-modal']) {
        this.$refs['unlink-account-modal'].hide()
      }
      this.showUnlinkAccountModalBool = false
    },
    unlinkConnection() {
      CalendarApi.unlinkAccount().then(() => {
        this.addEmail = false
        this.addCalendar = false
        this.userConfig = null
        this.emailAddress = ''
        this.calendarOfUser = null
        this.hideUnlinkAccountModal()
      }).catch((error) => {
        console.log(error)
        this.hideUnlinkAccountModal()
      })
    },
    hideCreateCalendarModal() {
      if (this.$refs['create-calendar-modal']) {
        this.$refs['create-calendar-modal'].hide()
      }
      this.showCreateCalendarModal = false
      this.calendarForCreation = ''
    },
    async setCalendar() {
      this.savingCalendar = true
      const selectedCalendar = this.availableCalendars.find(cal => cal.nylasCalendarId === this.calendarForCreation)
      let hasOldCalendar = false
      CalendarApi.setCalendar({ nylasCalendarId: selectedCalendar.nylasCalendarId }).then(response => {
        this.savingCalendar = false
        this.calendarOfUser = response.calendar
        if (hasOldCalendar) {
          Vue.toasted.show(this.$t('message.successMessages.calendarChanged'), { type: 'success' })
        } else {
          Vue.toasted.show(this.$t('message.successMessages.calendarCreation'), { type: 'success' })
        }
      }).catch(e => {
        console.log(e)
        this.savingCalendar = false
        Vue.toasted.show(this.$t('message.savingErrors.calendarCreation'), { type: 'error' })
      }).finally(() => {
        this.hideCreateCalendarModal()
      })
    },
    connectAccount() {
      this.loading = true
      const initialUrl = window.location.href
      const scopes = []
      if (this.addEmail || this.emailConnected) {
        scopes.push(NylasAuthScope.SCOPE_EMAIL)
      }
      if (this.addCalendar || this.calendarConnected) {
        scopes.push(NylasAuthScope.SCOPE_CALENDAR)
      }
      CalendarApi.startAuthNylas(scopes, initialUrl)
        .then(response => {
          if (response.authUrl) {
            if (scopes.includes(NylasAuthScope.SCOPE_CALENDAR) && !this.calendarConnected) {
              localStorage.setItem('startNylasAuth', 'true')
            }
            window.open(response.authUrl, '_self')
          }
        })
    }
  }
})
</script>
